/**
 * 远期房态图表数据转换
 * 基于基础数据（来自后端 API 或前端直调 getAliasPriceQuantity）按对比类型生成图表所需的 series / dataset
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { AXIS_LABEL_INTEGER } from '../../../configs/tooltipCommon';
import { COMPARISON_REFERENCE } from '@ms/shared-components';
function extractComparisonArray(raw, fallbackLabel, defaultColor) {
    if (Array.isArray(raw)) {
        return { value: raw, color: defaultColor, name: fallbackLabel };
    }
    if (raw && typeof raw === "object" && "value" in raw) {
        var o = raw;
        return {
            value: o.value,
            color: o.color || defaultColor,
            name: o.label || fallbackLabel,
        };
    }
    return { value: [], color: defaultColor, name: fallbackLabel };
}
/**
 * 生成远期房态图表数据：年同比、周环比两条辅助线同时输出，由 ECharts 图例控制显隐
 */
export var createRoomStatusRecentlyChartData = function (baseData) {
    var _a, _b, _c;
    var yoy = extractComparisonArray((_a = baseData.comparison) === null || _a === void 0 ? void 0 : _a.yearOverYear, COMPARISON_REFERENCE.yearOverYear.label, COMPARISON_REFERENCE.yearOverYear.color);
    var wow = extractComparisonArray((_b = baseData.comparison) === null || _b === void 0 ? void 0 : _b.weekOverWeek, COMPARISON_REFERENCE.weekOverWeek.label, COMPARISON_REFERENCE.weekOverWeek.color);
    var baseSeries = baseData.baseSeries || baseData.series || [];
    if (process.env.NODE_ENV === "development") {
        var klineSeries = baseSeries.find(function (s) {
            return s.type === "candlestick" || s.id === "kline";
        });
        if (klineSeries) {
            console.log("[createRoomStatusRecentlyChartData] K线图系列找到", {
                id: klineSeries.id,
                type: klineSeries.type,
                dataKey: klineSeries.dataKey,
                hasData: !!((_c = baseData.dataset) === null || _c === void 0 ? void 0 : _c[klineSeries.dataKey]),
            });
        }
        else {
            console.warn("[createRoomStatusRecentlyChartData] K线图系列未在baseSeries中找到", {
                baseSeriesTypes: baseSeries.map(function (s) { return ({
                    id: s.id,
                    type: s.type,
                    dataKey: s.dataKey,
                }); }),
            });
        }
    }
    var mappedSeries = baseSeries.map(function (s, i) {
        var _a;
        var dataKey = s.dataKey;
        if (dataKey && String(dataKey).startsWith("remainingRooms.")) {
            var roomType = String(dataKey).replace("remainingRooms.", "");
            dataKey = "remaining_".concat(roomType);
        }
        if (dataKey && String(dataKey).startsWith("occupiedRooms.")) {
            var roomType = String(dataKey).replace("occupiedRooms.", "");
            dataKey = "occupied_".concat(roomType);
        }
        return __assign(__assign({}, s), { id: (_a = s.id) !== null && _a !== void 0 ? _a : "series-".concat(i), stack: s.stack || s.stackId, dataKey: dataKey, showInLegend: false });
    });
    var baseDataset = baseData.dataset ? __assign({}, baseData.dataset) : {};
    var result = {
        categories: baseData.categories,
        dataset: __assign(__assign({}, baseDataset), { comparisonYearOverYear: yoy.value, comparisonWeekOverWeek: wow.value }),
        axes: baseData.axes,
        series: __spreadArray(__spreadArray([], mappedSeries, true), [
            {
                id: "comparison-yoy",
                name: yoy.name,
                dataKey: "comparisonYearOverYear",
                type: "line",
                axisId: "right",
                color: COMPARISON_REFERENCE.yearOverYear.color,
                showInLegend: true,
                lineStyle: __assign({}, COMPARISON_REFERENCE.yearOverYear.lineStyle),
            },
            {
                id: "comparison-wow",
                name: wow.name,
                dataKey: "comparisonWeekOverWeek",
                type: "line",
                axisId: "right",
                color: COMPARISON_REFERENCE.weekOverWeek.color,
                showInLegend: true,
                lineStyle: __assign({}, COMPARISON_REFERENCE.weekOverWeek.lineStyle),
            },
        ], false),
    };
    return result;
};
var EMPTY_BASE_CHART_DATA = {
    categories: [],
    dataset: {
        stayingRooms: [],
        remainingRooms: {},
        totalRooms: [],
        klineData: [],
        averagePrice: [],
        highestPrice: [],
        lowestPrice: [],
        yearOverYear: [],
        weekOverWeek: [],
    },
    axes: {
        left: { id: 'left', position: 'left', name: '房间数', unit: '间', min: 0, max: 500 },
        right: { id: 'right', position: 'right', name: '价格', unit: '元', min: 0, max: 600 },
    },
    baseSeries: [],
    comparison: {
        yearOverYear: {
            value: [],
            color: COMPARISON_REFERENCE.yearOverYear.color,
            strokeDasharray: COMPARISON_REFERENCE.yearOverYear.strokeDasharray,
            label: COMPARISON_REFERENCE.yearOverYear.label,
        },
        weekOverWeek: {
            value: [],
            color: COMPARISON_REFERENCE.weekOverWeek.color,
            strokeDasharray: COMPARISON_REFERENCE.weekOverWeek.strokeDasharray,
            label: COMPARISON_REFERENCE.weekOverWeek.label,
        },
    },
};
/**
 * 将 getAliasPriceQuantity 接口返回转为远期房态组件所需的 baseChartData
 */
export function aliasPriceQuantityToBaseChartData(response) {
    if (!response.data || response.data.length === 0) {
        return __assign({}, EMPTY_BASE_CHART_DATA);
    }
    var data = response.data;
    var categories = data.map(function (d) {
        var date = new Date(d.date * 1000);
        return "".concat(date.getMonth() + 1, "/").concat(date.getDate());
    });
    var allAliasNames = Array.from(new Set(data.flatMap(function (day) { return day.aliases.map(function (a) { return a.aliasName; }); })));
    var remainingRooms = {};
    allAliasNames.forEach(function (name) {
        remainingRooms[name] = data.map(function (day) {
            var _a;
            var a = day.aliases.find(function (x) { return x.aliasName === name; });
            return (_a = a === null || a === void 0 ? void 0 : a.quantityOpen) !== null && _a !== void 0 ? _a : 0;
        });
    });
    var stayingRooms = data.map(function (day) {
        return day.aliases.reduce(function (sum, a) {
            var _a, _b;
            var total = (_a = a.totalQuantity) !== null && _a !== void 0 ? _a : 0;
            return sum + (total - ((_b = a.quantityOpen) !== null && _b !== void 0 ? _b : 0));
        }, 0);
    });
    var totalRooms = data.map(function (day) {
        return day.aliases.reduce(function (sum, a) { var _a; return sum + ((_a = a.totalQuantity) !== null && _a !== void 0 ? _a : 0); }, 0);
    });
    var dayPrices = data.map(function (day) {
        var prices = day.aliases.map(function (a) { var _a; return (_a = a.currentPrice) !== null && _a !== void 0 ? _a : 0; }).filter(function (p) { return p > 0; });
        var fromAliases = prices.length ? { min: Math.min.apply(Math, prices), max: Math.max.apply(Math, prices) } : { min: 0, max: 0 };
        var min = day.priceMin != null && Number.isFinite(day.priceMin) ? day.priceMin : fromAliases.min;
        var max = day.priceMax != null && Number.isFinite(day.priceMax) ? day.priceMax : fromAliases.max;
        return { min: min, max: max };
    });
    var klineData = dayPrices.map(function (_a) {
        var min = _a.min, max = _a.max;
        return [
            min,
            max,
            min,
            max,
        ];
    });
    var averagePrice = data.map(function (day) {
        if (!day.aliases.length)
            return 0;
        var sum = day.aliases.reduce(function (s, a) { var _a; return s + ((_a = a.currentPrice) !== null && _a !== void 0 ? _a : 0); }, 0);
        return Math.round(sum / day.aliases.length);
    });
    var highestPrice = dayPrices.map(function (p) { return p.max; });
    var lowestPrice = dayPrices.map(function (p) { return p.min; });
    var baseSeries = __spreadArray(__spreadArray([
        { id: 'staying', name: '已占用', dataKey: 'stayingRooms', type: 'bar', axisId: 'left', stackId: 'room' }
    ], allAliasNames.map(function (name) { return ({
        id: "remaining_".concat(name),
        name: "".concat(name, "(\u53EF\u552E)"),
        dataKey: "remainingRooms.".concat(name),
        type: 'bar',
        axisId: 'left',
        stackId: 'room',
    }); }), true), [
        { id: 'totalRooms', name: '总房量', dataKey: 'totalRooms', type: 'line', axisId: 'left' },
        { id: 'kline', name: '价格', dataKey: 'klineData', type: 'candlestick', axisId: 'right' },
    ], false);
    var maxPrice = Math.max.apply(Math, __spreadArray(__spreadArray([], dayPrices.map(function (p) { return p.max; }), false), [1], false));
    var maxTotalRooms = Math.max.apply(Math, __spreadArray(__spreadArray([], totalRooms, false), [1], false));
    var leftAxisMax = Math.ceil(maxTotalRooms * 2);
    var rightAxisMax = maxPrice > 600 ? Math.ceil(maxPrice * 1.05) : 600;
    return {
        categories: categories,
        dataset: {
            stayingRooms: stayingRooms,
            remainingRooms: remainingRooms,
            totalRooms: totalRooms,
            klineData: klineData,
            averagePrice: averagePrice,
            highestPrice: highestPrice,
            lowestPrice: lowestPrice,
            yearOverYear: [],
            weekOverWeek: [],
        },
        axes: {
            left: { id: 'left', position: 'left', name: '房间数', unit: '间', min: 0, max: leftAxisMax },
            right: { id: 'right', position: 'right', name: '价格', unit: '元', min: 0, max: rightAxisMax },
        },
        baseSeries: baseSeries,
        comparison: {
            yearOverYear: {
                value: [],
                color: COMPARISON_REFERENCE.yearOverYear.color,
                strokeDasharray: COMPARISON_REFERENCE.yearOverYear.strokeDasharray,
                label: COMPARISON_REFERENCE.yearOverYear.label,
            },
            weekOverWeek: {
                value: [],
                color: COMPARISON_REFERENCE.weekOverWeek.color,
                strokeDasharray: COMPARISON_REFERENCE.weekOverWeek.strokeDasharray,
                label: COMPARISON_REFERENCE.weekOverWeek.label,
            },
        },
    };
}
/**
 * 将 FSM futureRoomStatusList 转为与 aliasPriceQuantity 一致的 baseChartData
 */
export function buildBaseChartDataFromFutureRoomStatusList(list) {
    if (!(list === null || list === void 0 ? void 0 : list.length))
        return __assign({}, EMPTY_BASE_CHART_DATA);
    var hasDayRowsWithVo = list.some(function (r) {
        return r.date != null &&
            Array.isArray(r.futureRoomStatusVoList) &&
            r.futureRoomStatusVoList.length > 0;
    });
    var sorted;
    var totalRooms;
    var stayingRooms;
    var allAliasNames;
    var remainingRooms;
    var occupiedRooms;
    if (hasDayRowsWithVo) {
        var dayRows = list.filter(function (row) {
            return row.date != null &&
                Array.isArray(row.futureRoomStatusVoList) &&
                row.futureRoomStatusVoList.length > 0;
        });
        sorted = __spreadArray([], dayRows, true).sort(function (a, b) { return (a.date || '').localeCompare(b.date || ''); });
        totalRooms = sorted.map(function (d) { var _a; return (_a = d.allCount) !== null && _a !== void 0 ? _a : 0; });
        stayingRooms = sorted.map(function (d) { var _a, _b; return ((_a = d.allCount) !== null && _a !== void 0 ? _a : 0) - ((_b = d.canSellCount) !== null && _b !== void 0 ? _b : 0); });
        allAliasNames = Array.from(new Set(sorted.flatMap(function (d) {
            var _a;
            return ((_a = d.futureRoomStatusVoList) !== null && _a !== void 0 ? _a : [])
                .map(function (v) { return v.aliasName; })
                .filter(function (x) { return x != null && x !== ''; });
        })));
        remainingRooms = {};
        occupiedRooms = {};
        allAliasNames.forEach(function (name) {
            remainingRooms[name] = sorted.map(function (day) {
                var _a, _b;
                var vo = ((_a = day.futureRoomStatusVoList) !== null && _a !== void 0 ? _a : []).find(function (v) { return v.aliasName === name; });
                return (_b = vo === null || vo === void 0 ? void 0 : vo.canSellCount) !== null && _b !== void 0 ? _b : 0;
            });
            occupiedRooms[name] = sorted.map(function (day) {
                var _a, _b, _c;
                var vo = ((_a = day.futureRoomStatusVoList) !== null && _a !== void 0 ? _a : []).find(function (v) { return v.aliasName === name; });
                var all = (_b = vo === null || vo === void 0 ? void 0 : vo.allCount) !== null && _b !== void 0 ? _b : 0;
                var can = (_c = vo === null || vo === void 0 ? void 0 : vo.canSellCount) !== null && _c !== void 0 ? _c : 0;
                return all - can;
            });
        });
    }
    else {
        var byDate_1 = new Map();
        list.forEach(function (row) {
            if (row.date == null)
                return;
            if (!byDate_1.has(row.date))
                byDate_1.set(row.date, []);
            byDate_1.get(row.date).push(row);
        });
        var dates_1 = Array.from(byDate_1.keys()).sort();
        if (dates_1.length === 0)
            return __assign({}, EMPTY_BASE_CHART_DATA);
        sorted = dates_1.map(function (date) {
            var rows = byDate_1.get(date);
            var first = rows[0];
            var total = rows.reduce(function (s, r) { var _a; return s + ((_a = r.allCount) !== null && _a !== void 0 ? _a : 0); }, 0);
            var canSell = rows.reduce(function (s, r) { var _a; return s + ((_a = r.canSellCount) !== null && _a !== void 0 ? _a : 0); }, 0);
            return __assign(__assign({}, first), { allCount: total, canSellCount: canSell });
        });
        totalRooms = sorted.map(function (d) { var _a; return (_a = d.allCount) !== null && _a !== void 0 ? _a : 0; });
        stayingRooms = sorted.map(function (d) { var _a, _b; return ((_a = d.allCount) !== null && _a !== void 0 ? _a : 0) - ((_b = d.canSellCount) !== null && _b !== void 0 ? _b : 0); });
        allAliasNames = Array.from(new Set(list.map(function (r) { return r.aliasName; }).filter(function (x) { return x != null && x !== ''; })));
        remainingRooms = {};
        occupiedRooms = {};
        allAliasNames.forEach(function (name) {
            remainingRooms[name] = dates_1.map(function (date) {
                var _a, _b;
                var row = (_a = byDate_1.get(date)) === null || _a === void 0 ? void 0 : _a.find(function (r) { return r.aliasName === name; });
                return (_b = row === null || row === void 0 ? void 0 : row.canSellCount) !== null && _b !== void 0 ? _b : 0;
            });
            occupiedRooms[name] = dates_1.map(function (date) {
                var _a, _b, _c;
                var row = (_a = byDate_1.get(date)) === null || _a === void 0 ? void 0 : _a.find(function (r) { return r.aliasName === name; });
                var all = (_b = row === null || row === void 0 ? void 0 : row.allCount) !== null && _b !== void 0 ? _b : 0;
                var can = (_c = row === null || row === void 0 ? void 0 : row.canSellCount) !== null && _c !== void 0 ? _c : 0;
                return all - can;
            });
        });
    }
    var categories = sorted.map(function (d) {
        var _a;
        var parts = (d.date || '').split('-').map(Number);
        if (parts.length >= 2)
            return "".concat(parts[1], "/").concat((_a = parts[2]) !== null && _a !== void 0 ? _a : 0);
        return d.date || '';
    });
    var n = sorted.length;
    var klineData = Array(n).fill([0, 0, 0, 0]);
    var averagePrice = Array(n).fill(0);
    var highestPrice = Array(n).fill(0);
    var lowestPrice = Array(n).fill(0);
    var maxTotal = Math.max.apply(Math, __spreadArray(__spreadArray([], totalRooms, false), [1], false));
    var leftAxisMax = Math.ceil(maxTotal * 2);
    var baseSeries = __spreadArray(__spreadArray([], (allAliasNames.length > 0
        ? __spreadArray(__spreadArray([], allAliasNames.map(function (name) { return ({
            id: "occupied_".concat(name),
            name: "".concat(name, "(\u5DF2\u5360\u7528)"),
            dataKey: "occupiedRooms.".concat(name),
            type: 'bar',
            axisId: 'left',
            stackId: 'room',
        }); }), true), allAliasNames.map(function (name) { return ({
            id: "remaining_".concat(name),
            name: "".concat(name, "(\u53EF\u552E)"),
            dataKey: "remainingRooms.".concat(name),
            type: 'bar',
            axisId: 'left',
            stackId: 'room',
        }); }), true) : [{ id: 'staying', name: '已占用', dataKey: 'stayingRooms', type: 'bar', axisId: 'left', stackId: 'room' }]), true), [
        { id: 'totalRooms', name: '总房量', dataKey: 'totalRooms', type: 'line', axisId: 'left' },
        { id: 'kline', name: '价格', dataKey: 'klineData', type: 'candlestick', axisId: 'right' },
    ], false);
    return {
        categories: categories,
        dataset: {
            stayingRooms: stayingRooms,
            remainingRooms: remainingRooms,
            occupiedRooms: occupiedRooms,
            totalRooms: totalRooms,
            klineData: klineData,
            averagePrice: averagePrice,
            highestPrice: highestPrice,
            lowestPrice: lowestPrice,
            yearOverYear: [],
            weekOverWeek: [],
        },
        axes: {
            left: { id: 'left', position: 'left', name: '房间数', unit: '间', min: 0, max: leftAxisMax },
            right: { id: 'right', position: 'right', name: '价格', unit: '元', min: 0, max: 600 },
        },
        baseSeries: baseSeries,
        comparison: {
            yearOverYear: {
                value: [],
                color: COMPARISON_REFERENCE.yearOverYear.color,
                strokeDasharray: COMPARISON_REFERENCE.yearOverYear.strokeDasharray,
                label: COMPARISON_REFERENCE.yearOverYear.label,
            },
            weekOverWeek: {
                value: [],
                color: COMPARISON_REFERENCE.weekOverWeek.color,
                strokeDasharray: COMPARISON_REFERENCE.weekOverWeek.strokeDasharray,
                label: COMPARISON_REFERENCE.weekOverWeek.label,
            },
        },
    };
}
var STACK_COLORS = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4'];
/**
 * 从 FSM futureRoomStatusList 构建各房型累计图（堆叠柱状）
 */
export function buildRoomTypeStackedChartFromFutureRoomStatusList(list, mode) {
    if (!(list === null || list === void 0 ? void 0 : list.length))
        return null;
    var hasDayRowsWithVo = list.some(function (r) {
        return r.date != null &&
            Array.isArray(r.futureRoomStatusVoList) &&
            r.futureRoomStatusVoList.length > 0;
    });
    var categories;
    var allAliasNames;
    var dataset;
    if (hasDayRowsWithVo) {
        var dayRows = list.filter(function (row) {
            return row.date != null &&
                Array.isArray(row.futureRoomStatusVoList) &&
                row.futureRoomStatusVoList.length > 0;
        });
        var sorted_1 = __spreadArray([], dayRows, true).sort(function (a, b) { return (a.date || '').localeCompare(b.date || ''); });
        categories = sorted_1.map(function (d) {
            var _a;
            var parts = (d.date || '').split('-').map(Number);
            if (parts.length >= 2)
                return "".concat(parts[1], "/").concat((_a = parts[2]) !== null && _a !== void 0 ? _a : 0);
            return d.date || '';
        });
        allAliasNames = Array.from(new Set(sorted_1.flatMap(function (d) {
            var _a;
            return ((_a = d.futureRoomStatusVoList) !== null && _a !== void 0 ? _a : [])
                .map(function (v) { return v.aliasName; })
                .filter(function (x) { return x != null && x !== ''; });
        })));
        dataset = {};
        allAliasNames.forEach(function (aliasName) {
            dataset[aliasName] = sorted_1.map(function (day) {
                var _a, _b, _c, _d;
                var vo = ((_a = day.futureRoomStatusVoList) !== null && _a !== void 0 ? _a : []).find(function (v) { return v.aliasName === aliasName; });
                if (!vo)
                    return 0;
                if (mode === 'canSell')
                    return (_b = vo.canSellCount) !== null && _b !== void 0 ? _b : 0;
                return ((_c = vo.allCount) !== null && _c !== void 0 ? _c : 0) - ((_d = vo.canSellCount) !== null && _d !== void 0 ? _d : 0);
            });
        });
    }
    else {
        var byDateAll_1 = new Map();
        list.forEach(function (row) {
            if (row.date == null)
                return;
            if (!byDateAll_1.has(row.date))
                byDateAll_1.set(row.date, []);
            byDateAll_1.get(row.date).push(row);
        });
        var dates_2 = Array.from(byDateAll_1.keys()).sort(function (a, b) { return a.localeCompare(b); });
        if (dates_2.length === 0)
            return null;
        categories = dates_2.map(function (date) {
            var _a;
            var parts = date.split('-').map(Number);
            if (parts.length >= 2)
                return "".concat(parts[1], "/").concat((_a = parts[2]) !== null && _a !== void 0 ? _a : 0);
            return date;
        });
        var flatAliasNames = Array.from(new Set(list.map(function (r) { return r.aliasName; }).filter(function (x) { return x != null && x !== ''; })));
        if (flatAliasNames.length > 0) {
            allAliasNames = flatAliasNames;
            dataset = {};
            allAliasNames.forEach(function (aliasName) {
                dataset[aliasName] = dates_2.map(function (date) {
                    var _a, _b, _c, _d;
                    var row = (_a = byDateAll_1.get(date)) === null || _a === void 0 ? void 0 : _a.find(function (r) { return r.aliasName === aliasName; });
                    if (!row)
                        return 0;
                    if (mode === 'canSell')
                        return (_b = row.canSellCount) !== null && _b !== void 0 ? _b : 0;
                    return ((_c = row.allCount) !== null && _c !== void 0 ? _c : 0) - ((_d = row.canSellCount) !== null && _d !== void 0 ? _d : 0);
                });
            });
        }
        else {
            allAliasNames = ['全部'];
            dataset = {
                全部: dates_2.map(function (date) {
                    var _a, _b, _c, _d;
                    var rows = (_a = byDateAll_1.get(date)) !== null && _a !== void 0 ? _a : [];
                    var first = rows[0];
                    if (!first)
                        return 0;
                    if (mode === 'canSell')
                        return (_b = first.canSellCount) !== null && _b !== void 0 ? _b : 0;
                    return ((_c = first.allCount) !== null && _c !== void 0 ? _c : 0) - ((_d = first.canSellCount) !== null && _d !== void 0 ? _d : 0);
                }),
            };
        }
    }
    var maxVal = Math.max.apply(Math, __spreadArray(__spreadArray([], allAliasNames.flatMap(function (name) { var _a; return (_a = dataset[name]) !== null && _a !== void 0 ? _a : []; }), false), [1], false));
    var leftMax = Math.ceil(maxVal * 1.2);
    var series = allAliasNames.map(function (name, i) { return ({
        id: "room_".concat(name),
        name: name,
        dataKey: name,
        type: 'bar',
        axisId: 'left',
        stackId: 'byRoomType',
        color: STACK_COLORS[i % STACK_COLORS.length],
    }); });
    return {
        categories: categories,
        dataset: dataset,
        axes: {
            left: {
                id: 'left',
                position: 'left',
                name: mode === 'canSell' ? '可售房量' : '已占用房量',
                unit: '间',
                min: 0,
                max: leftMax,
                axisLabel: AXIS_LABEL_INTEGER,
            },
        },
        series: series,
    };
}
